import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { serviceDeliveryAPI } from '../lib/api';
import { OPT_TIPOS, OPT_CONCLUSIONES } from '../lib/optimizations';
import { FlaskConical, Loader2, ArrowRight, Filter, Lightbulb } from 'lucide-react';

const tipoLabel = (v) => OPT_TIPOS.find((t) => t.v === v)?.label || v;
const concInfo = (v) => OPT_CONCLUSIONES.find((c) => c.v === v);
const eur = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function ResultBar({ stats }) {
  const total = stats.funciono + stats.no_funciono + stats.neutro;
  if (total === 0) return <p className="text-[11px] text-muted-foreground italic">Sin conclusiones aún</p>;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-muted w-full">
      <div className="bg-emerald-500" style={{ width: pct(stats.funciono) }} />
      <div className="bg-red-500" style={{ width: pct(stats.no_funciono) }} />
      <div className="bg-gray-400" style={{ width: pct(stats.neutro) }} />
    </div>
  );
}

function GroupStats({ title, groups, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-3"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Sin datos todavía.</p>
        ) : groups.map((g) => (
          <div key={g.key} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">{g.label}</span>
              <span className="text-xs text-muted-foreground">
                <span className="text-emerald-600 font-semibold">{g.funciono} ✓</span>
                {' · '}<span className="text-red-600 font-semibold">{g.no_funciono} ✗</span>
                {' · '}<span className="text-gray-500">{g.neutro} =</span>
                {g.pendiente > 0 && <span> · {g.pendiente} sin concluir</span>}
              </span>
            </div>
            <ResultBar stats={g} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function buildGroups(entries, keyFn, labelFn) {
  const map = {};
  entries.forEach((e) => {
    const k = keyFn(e);
    if (!map[k]) map[k] = { key: k, label: labelFn(e, k), funciono: 0, no_funciono: 0, neutro: 0, pendiente: 0, total: 0 };
    map[k].total += 1;
    if (e.conclusion) map[k][e.conclusion] += 1; else map[k].pendiente += 1;
  });
  return Object.values(map).sort((a, b) => (b.funciono - b.no_funciono) - (a.funciono - a.no_funciono) || b.total - a.total);
}

export default function LearningsPage() {
  const [all, setAll] = useState(null);
  const [fSector, setFSector] = useState('all');
  const [fCliente, setFCliente] = useState('all');
  const [fTipo, setFTipo] = useState('all');
  const [fConc, setFConc] = useState('all');

  useEffect(() => {
    serviceDeliveryAPI.globalCampaignOptimizations()
      .then((r) => setAll(r?.optimizations || []))
      .catch(() => setAll([]));
  }, []);

  const sectores = useMemo(() => [...new Set((all || []).map((o) => o.sector).filter(Boolean))].sort(), [all]);
  const clientes = useMemo(() => [...new Set((all || []).map((o) => o.client_name))].sort(), [all]);

  const entries = useMemo(() => (all || []).filter((o) =>
    (fSector === 'all' || (fSector === 'sin' ? !o.sector : o.sector === fSector)) &&
    (fCliente === 'all' || o.client_name === fCliente) &&
    (fTipo === 'all' || o.tipo === fTipo) &&
    (fConc === 'all' || (o.conclusion || 'pendiente') === fConc)
  ), [all, fSector, fCliente, fTipo, fConc]);

  const byTipo = useMemo(() => buildGroups(entries, (e) => e.tipo, (e) => tipoLabel(e.tipo)), [entries]);
  const bySector = useMemo(() => buildGroups(entries, (e) => e.sector || 'sin', (e) => e.sector || 'Sin sector'), [entries]);
  const byCliente = useMemo(() => buildGroups(entries, (e) => e.client_name, (e) => e.client_name), [entries]);

  if (all === null) return <div className="py-24 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-muted-foreground" /></div>;

  const funciono = entries.filter((e) => e.conclusion === 'funciono').length;
  const concluidas = entries.filter((e) => e.conclusion).length;

  return (
    <div className="space-y-6" data-testid="learnings-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><FlaskConical className="w-7 h-7 text-primary" /> Aprendizajes de Ads</h1>
        <p className="text-sm text-muted-foreground mt-1">Todas las optimizaciones registradas en las campañas de todos los clientes, para comparar qué funciona.</p>
      </div>

      {all.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center space-y-2" data-testid="learnings-empty">
            <FlaskConical className="w-9 h-9 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium">Aún no hay optimizaciones registradas.</p>
            <p className="text-sm text-muted-foreground">Regístralas desde la ficha de cada cliente → ADS → "Añadir optimización". Aquí se irán acumulando para comparar.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="learnings-filters">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select value={fSector} onChange={(e) => setFSector(e.target.value)} className="h-8 rounded-md border px-2 text-xs bg-background" data-testid="learnings-filter-sector">
              <option value="all">Todos los sectores</option>
              {sectores.map((s) => <option key={s} value={s}>{s}</option>)}
              <option value="sin">Sin sector</option>
            </select>
            <select value={fCliente} onChange={(e) => setFCliente(e.target.value)} className="h-8 rounded-md border px-2 text-xs bg-background" data-testid="learnings-filter-cliente">
              <option value="all">Todos los clientes</option>
              {clientes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} className="h-8 rounded-md border px-2 text-xs bg-background" data-testid="learnings-filter-tipo">
              <option value="all">Todos los cambios</option>
              {OPT_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <select value={fConc} onChange={(e) => setFConc(e.target.value)} className="h-8 rounded-md border px-2 text-xs bg-background" data-testid="learnings-filter-conclusion">
              <option value="all">Todas las conclusiones</option>
              {OPT_CONCLUSIONES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              <option value="pendiente">Sin conclusión</option>
            </select>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="learnings-count">
              {entries.length} optimización{entries.length !== 1 ? 'es' : ''}{concluidas > 0 && ` · ${Math.round((funciono / concluidas) * 100)}% funcionaron`}
            </span>
          </div>

          {sectores.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-center gap-2" data-testid="learnings-sector-hint">
              <Lightbulb className="w-4 h-4 shrink-0" />
              Ningún cliente tiene el "Sector" rellenado todavía. Complétalo en la ficha de cada cliente → Información Cliente para poder comparar por sector.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <GroupStats title="Por tipo de cambio" groups={byTipo} testid="learnings-by-tipo" />
            <GroupStats title="Por sector" groups={bySector} testid="learnings-by-sector" />
            <GroupStats title="Por cliente" groups={byCliente} testid="learnings-by-cliente" />
          </div>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Histórico completo</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {entries.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-6 text-center">Ninguna optimización coincide con los filtros.</p>
              ) : entries.map((e) => {
                const conc = concInfo(e.conclusion);
                const cplA = e.datos_antes?.cpl, cplD = e.datos_despues?.cpl;
                const cplColor = (cplA != null && cplD != null) ? (cplD < cplA ? 'text-emerald-600' : cplD > cplA ? 'text-red-600' : 'text-muted-foreground') : 'text-muted-foreground';
                return (
                  <div key={e.id} className="rounded-lg border p-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`learning-entry-${e.id}`}>
                    <span className="text-xs text-muted-foreground w-20 shrink-0">{fmtD(e.fecha)}</span>
                    <div className="min-w-[160px]">
                      <p className="text-sm font-medium">{e.client_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={e.campaign_name}>{e.sector ? `${e.sector} · ` : ''}{e.campaign_name || 'Campaña'}</p>
                    </div>
                    <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted/40 font-medium">{tipoLabel(e.tipo)}</span>
                    {e.que_cambie && <span className="text-xs text-muted-foreground truncate max-w-[260px]" title={e.que_cambie}>{e.que_cambie}</span>}
                    <span className={`text-xs inline-flex items-center gap-1 font-medium ml-auto ${cplColor}`}>
                      {cplA != null ? <>CPL {eur(cplA)} <ArrowRight className="w-3 h-3" /> {cplD != null ? eur(cplD) : '…'}</> : null}
                    </span>
                    {conc ? (
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${conc.cls}`}>{conc.label}</span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border bg-background text-muted-foreground">Sin conclusión</span>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
